import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../components/DataTable/BasicPagination';
import SectionWrapper from '../../../components/SectionWrapper/SectionWrapper';
import useRestaurantKitchenConfig from '../../../hooks/restaurant/useRestaurantKitchenConfig';
import useRestaurantMenu from '../../../hooks/restaurant/useRestaurantMenu';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import WorkspaceLayout from '../../../layout/WorkspaceLayout';
import BranchService from '../../../services/branch/BranchService';
import { useAuthUser } from '../../../hooks/auth/useAuthUser';

import { IBranchDropdown } from '../../../types/branches/branchInterfaces';
import { RestaurantMenuItemFilters } from '../../../types/restaurant/RestaurantMenuTypes';
import { RestaurantPageRequest } from '../../../types/restaurant/RestaurantRecipeTypes';

import MenuItemFormModal from './MenuItemFormModal';
import {
  MenuActiveFilter,
  MenuAvailabilityFilter,
  MenuFeaturedFilter,
} from './types';
import MenuBranchEmpty from './components/MenuBranchEmpty';
import MenuHeader from './components/MenuHeader';
import MenuFilters from './components/MenuFilters';
import MenuSkeleton from './components/MenuSkeleton';
import MenuEmpty from './components/MenuEmpty';
import MenuGrid from './components/MenuGrid';
import MenuTable from './components/MenuTable';
import MenuItemModifiersModal from './MenuItemModifiersModal';

const RestaurantMenuScreen = () => {
  // =====================================================
  // 🔹 Sucursales
  // =====================================================

  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);

  const [branchId, setBranchId] = useState<number | ''>('');

  // Ítem cuyo gestor de modificadores está abierto (encima del form).
  const [modifiersItem, setModifiersItem] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const [loadingBranches, setLoadingBranches] = useState(true);

  const [branchError, setBranchError] = useState('');

  // =====================================================
  // 🔹 Filtros
  // =====================================================

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);

  const [activeFilter, setActiveFilter] = useState<MenuActiveFilter>('ACTIVE');

  const [availabilityFilter, setAvailabilityFilter] =
    useState<MenuAvailabilityFilter>('ALL');

  const [featuredFilter, setFeaturedFilter] =
    useState<MenuFeaturedFilter>('ALL');

  // =====================================================
  // 🔹 Paginación
  // =====================================================

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(5);

  const [sort] = useState(['displayOrder,asc', 'displayName,asc', 'id,asc']);

  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const menu = useRestaurantMenu(branchId);
  const kitchenConfig = useRestaurantKitchenConfig(branchId);
  const navigate = useNavigate();

  const debouncedSearch = useDebouncedValue(search, 350);

  // =====================================================
  // 🔹 Cargar sucursales
  // =====================================================

  useEffect(() => {
    setBranchError('');

    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);

        if (items.length > 0) {
          setBranchId(items[0].id);
        }
      })
      .catch((error: any) => {
        setBranchError(
          error?.response?.data?.message ||
            'No fue posible cargar las sucursales.'
        );
      })
      .finally(() => setLoadingBranches(false));
  }, [canPickBranch]);

  // =====================================================
  // 🔹 Cargar opciones
  // =====================================================

  useEffect(() => {
    if (branchId) {
      menu.loadItemOptions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  // =====================================================
  // 🔹 Petición para backend
  // =====================================================

  const request = useMemo<RestaurantPageRequest<RestaurantMenuItemFilters>>(
    () => ({
      page,
      size,
      sort,
      filters: {
        search: debouncedSearch.trim() || null,

        categoryId: categoryId || null,

        active: activeFilter === 'ALL' ? null : activeFilter === 'ACTIVE',

        available:
          availabilityFilter === 'ALL'
            ? null
            : availabilityFilter === 'AVAILABLE',

        featured:
          featuredFilter === 'ALL' ? null : featuredFilter === 'FEATURED',
      },
    }),
    [
      activeFilter,
      availabilityFilter,
      categoryId,
      debouncedSearch,
      featuredFilter,
      page,
      size,
      sort,
    ]
  );

  // =====================================================
  // 🔹 Cargar menú
  // =====================================================

  useEffect(() => {
    if (branchId) {
      menu.loadItems(request);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, request]);

  // =====================================================
  // 🔹 Eventos
  // =====================================================

  const previousPage = () => {
    setPage((current) => Math.max(0, current - 1));
  };

  const resetFilters = () => {
    setSearch('');
    setCategoryId(0);
    setActiveFilter('ACTIVE');
    setAvailabilityFilter('ALL');
    setFeaturedFilter('ALL');
    setPage(0);
  };

  const changeBranch = (nextBranchId: number) => {
    resetFilters();
    setBranchId(nextBranchId);
  };

  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const changeCategory = (value: number) => {
    setCategoryId(value);
    setPage(0);
  };

  const changeActive = (value: MenuActiveFilter) => {
    setActiveFilter(value);
    setPage(0);
  };

  const changeAvailability = (value: MenuAvailabilityFilter) => {
    setAvailabilityFilter(value);
    setPage(0);
  };

  const changeFeatured = (value: MenuFeaturedFilter) => {
    setFeaturedFilter(value);
    setPage(0);
  };

  const changePageSize = (value: number) => {
    setSize(value);
    setPage(0);
  };

  const openConfiguration = () => {
    navigate('/restaurant/menu/configuration');
  };

  // Ya no exige categorías cargadas: se pueden crear al vuelo desde el modal.
  const createDisabled = !branchId;

  // =====================================================
  // 🔹 Sin sucursales
  // =====================================================

  if (!loadingBranches && !branchError && !branchId) {
    return (
      <WorkspaceLayout>
        <BreadcrumbTwo />

        <SectionWrapper>
          <MenuBranchEmpty />
        </SectionWrapper>
      </WorkspaceLayout>
    );
  }

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />

      <SectionWrapper>
        <MenuHeader
          branches={branches}
          branchId={branchId}
          loadingBranches={loadingBranches}
          branchError={branchError}
          canPickBranch={canPickBranch}
          createDisabled={createDisabled}
          onBranchChange={changeBranch}
          onConfigure={openConfiguration}
          onCreate={menu.openItem}
        />

        {branchId && (
          <>
            <MenuFilters
              search={search}
              categoryId={categoryId}
              activeFilter={activeFilter}
              availabilityFilter={availabilityFilter}
              featuredFilter={featuredFilter}
              categories={menu.categories}
              onSearchChange={changeSearch}
              onCategoryChange={changeCategory}
              onActiveChange={changeActive}
              onAvailabilityChange={changeAvailability}
              onFeaturedChange={changeFeatured}
            />

            {menu.loading ? (
              <MenuSkeleton />
            ) : menu.items.length === 0 ? (
              <MenuEmpty />
            ) : (
              <>
                <MenuTable
                  items={menu.items}
                  onEdit={menu.editItem}
                  onToggleAvailable={(item) =>
                    menu.toggleAvailable(item, request, previousPage)
                  }
                  onToggleActive={(item) =>
                    menu.toggleItemActive(item, request, previousPage)
                  }
                />
                <MenuGrid
                  items={menu.items}
                  onEdit={menu.editItem}
                  onToggleAvailable={(item) =>
                    menu.toggleAvailable(item, request, previousPage)
                  }
                  onToggleActive={(item) =>
                    menu.toggleItemActive(item, request, previousPage)
                  }
                />
              </>
            )}

            {menu.itemPage && menu.itemPage.page.totalElements > 0 && (
              <div className="border-t border-stroke px-5 pb-5 pt-4 dark:border-strokedark sm:px-7">
                <BasicPagination
                  currentPage={menu.itemPage.page.number}
                  totalPages={menu.itemPage.page.totalPages}
                  totalElements={menu.itemPage.page.totalElements}
                  size={menu.itemPage.page.size ?? size}
                  first={menu.itemPage.page.number === 0}
                  last={menu.itemPage.page.number >= menu.itemPage.page.totalPages - 1}
                  onPageChange={setPage}
                  onSizeChange={changePageSize}
                />
              </div>
            )}
          </>
        )}
      </SectionWrapper>

      <MenuItemFormModal
        isOpen={menu.itemModalOpen}
        form={menu.itemForm}
        categories={menu.categories}
        products={menu.products}
        stations={menu.stations}
        saving={menu.saving}
        onClose={menu.closeItem}
        onChange={menu.updateItemField}
        onCreateCategory={menu.createMenuCategoryInline}
        onCreateStation={menu.createStationInline}
        onReloadProducts={menu.loadItemOptions}
        requirePreparationTime={
          kitchenConfig.config.orderCompletionMode === 'TIMED'
        }
        onManageModifiers={
          menu.itemForm.id
            ? () =>
                setModifiersItem({
                  id: Number(menu.itemForm.id),
                  name:
                    menu.itemForm.displayName ||
                    menu.itemForm.productName ||
                    'Ítem',
                })
            : undefined
        }
        onSave={async () => {
          const wasNew = !menu.itemForm.id;
          const saved = await menu.saveItem(request, previousPage);
          if (wasNew && saved) {
            setModifiersItem({
              id: saved.id,
              name: saved.displayName || saved.productName || 'Ítem',
            });
          }
        }}
      />

      <MenuItemModifiersModal
        open={modifiersItem != null}
        menuItemId={modifiersItem?.id ?? null}
        menuItemName={modifiersItem?.name}
        branchId={Number(branchId) || 0}
        onClose={() => setModifiersItem(null)}
      />
    </WorkspaceLayout>
  );
};

export default RestaurantMenuScreen;
